import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, downloadFile } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { fmt, fmtInt, dateRangePresets } from "@/lib/format";
import { TrendingUp, TrendingDown, Wallet, Receipt, Package as PackageIcon, Users, Search, Download, Printer, Building2 } from "lucide-react";
import { toast } from "sonner";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";
import DiscountReport from "@/pages/reports/DiscountReport";
import VisitDurationReport from "@/pages/reports/VisitDurationReport";
import EmployeePerformanceReport from "@/pages/reports/EmployeePerformanceReport";
import ActiveServiceHoursReport from "@/pages/reports/ActiveServiceHoursReport";

const PRESETS = ["today", "month", "prev_month", "year", "prev_year", "custom"];

// Small print-action button reused by every report tab.
const PrintBtn = ({ onClick, lang }) => (
  <button onClick={onClick} data-testid="report-print-btn"
    className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)]">
    <Printer size={12}/>{lang === "ar" ? "طباعة التقرير" : "Print Report"}
  </button>
);

// Helper: build a "Period: from → to" meta row consistent across reports.
const periodMeta = (range, lang) => ([
  {
    label: lang === "ar" ? "الفترة" : "Period",
    value: `${range.start || "—"} → ${range.end || "—"}`,
  },
]);

const RangePicker = ({ range, setRange, preset, setPreset }) => {
  const { t } = useLanguage();
  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      const r = dateRangePresets(p);
      setRange(r);
    }
  };
  return (
    <div className="card-surface p-4 flex flex-wrap items-end gap-3">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(p => (
          <button key={p} onClick={() => onPreset(p)} data-testid={`preset-${p}`}
            className={`px-3 py-2 rounded-md text-sm border ${preset === p ? "btn-gold" : "border-[var(--border)] text-[var(--text)] bg-[var(--surface)]"}`}>
            {t(`preset_${p}`)}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2 ms-auto">
        <div>
          <label className="text-xs text-[var(--text-muted)] block mb-1">{t("from_date")}</label>
          <input type="date" className="input-dark" value={range.start} onChange={e => { setRange({...range, start: e.target.value}); setPreset("custom"); }} data-testid="range-from"/>
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] block mb-1">{t("to_date")}</label>
          <input type="date" className="input-dark" value={range.end} onChange={e => { setRange({...range, end: e.target.value}); setPreset("custom"); }} data-testid="range-to"/>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = "gold", testId }) => (
  <div className="card-surface p-5 flex items-center justify-between" data-testid={testId}>
    <div>
      <div className="text-xs uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">{label}</div>
      <div className={`font-heading text-3xl num-display ${accent === "gold" ? "gold-text" : accent === "rose" ? "rose-text" : "text-[var(--text)]"}`}>{value}</div>
    </div>
    <div className={`w-12 h-12 rounded-md flex items-center justify-center border ${accent === "rose" ? "border-[var(--rose)]" : "border-[var(--gold)]"}`}>
      <Icon size={22} className={accent === "rose" ? "text-[var(--rose)]" : "text-[var(--gold)]"}/>
    </div>
  </div>
);

// ============== ACCOUNTS REPORT ==============
const AccountsReport = ({ range, branch, template }) => {
  const { t, lang } = useLanguage();
  const [pl, setPL] = useState(null);
  const [rev, setRev] = useState(null);
  const [exp, setExp] = useState(null);

  const load = useCallback(async () => {
    const params = { start: range.start, end: range.end };
    if (branch) params.branch = branch;
    const [a, b, c] = await Promise.all([
      api.get("/reports/profit-loss", { params }),
      api.get("/reports/revenue", { params }),
      api.get("/reports/expenses", { params }),
    ]);
    setPL(a.data); setRev(b.data); setExp(c.data);
  }, [range.start, range.end, branch]);
  useEffect(() => { load(); }, [load]);

  // Build a single, self-contained report payload that mirrors what the
  // operator sees on screen (P&L summary + revenue-by-method + expenses-by
  // category). Print is opt-in per tab so each sub-report only ships its
  // own data — no full-screen capture.
  const onPrint = () => {
    if (!pl || !rev || !exp) return;
    const isAr = lang === "ar";
    printReport({
      title: t("reports_accounts"),
      subtitle: isAr ? "ملخص الحسابات والأرباح والمصروفات" : "Accounts, P&L & expenses summary",
      lang,
      template,
      meta: periodMeta(range, lang),
      columns: [isAr ? "البند" : "Item", isAr ? "القيمة" : "Value"],
      rows: [
        [t("revenue"), { value: fmt(pl.revenue), className: "num" }],
        [t("cogs"), { value: fmt(pl.cogs), className: "num neg" }],
        [t("gross_profit"), { value: fmt(pl.gross_profit), className: "num pos" }],
        [t("gross_margin"), { value: `${fmt(pl.gross_margin_pct, 1)}%`, className: "num" }],
        [t("operating_expenses"), { value: fmt(pl.expenses), className: "num neg" }],
        [t("net_profit"), { value: fmt(pl.net_profit), className: "num pos" }],
        [t("net_margin"), { value: `${fmt(pl.net_margin_pct, 1)}%`, className: "num" }],
      ],
      totals: [
        { label: t("revenue"), value: fmt(pl.revenue), className: "gold" },
        { label: t("net_profit"), value: fmt(pl.net_profit), className: "gold" },
        { label: t("invoices"), value: fmtInt(rev.count) },
      ],
      footerNote: isAr
        ? `طرق الدفع: ${rev.by_method.map(m => `${t(m.method) || m.method} ${fmt(m.amount)}`).join(" · ")}`
        : `Methods: ${rev.by_method.map(m => `${m.method} ${fmt(m.amount)}`).join(" · ")}`,
    });
  };

  if (!pl || !rev || !exp) return <div className="text-[var(--text-muted)]">{t("loading")}</div>;

  return (
    <div className="space-y-5">
      <div className="flex justify-end no-print"><PrintBtn onClick={onPrint} lang={lang}/></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label={t("revenue")} value={fmt(pl.revenue)} testId="rep-revenue"/>
        <StatCard icon={TrendingDown} label={t("cogs")} value={fmt(pl.cogs)} accent="rose"/>
        <StatCard icon={Wallet} label={t("gross_profit")} value={fmt(pl.gross_profit)}/>
        <StatCard icon={Receipt} label={t("gross_margin")} value={`${fmt(pl.gross_margin_pct, 1)}%`} accent="rose"/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={TrendingDown} label={t("operating_expenses")} value={fmt(pl.expenses)} accent="rose"/>
        <StatCard icon={Wallet} label={t("net_profit")} value={fmt(pl.net_profit)} testId="rep-profit"/>
        <StatCard icon={Receipt} label={t("net_margin")} value={`${fmt(pl.net_margin_pct, 1)}%`}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-surface p-5">
          <h3 className="font-heading text-lg text-[var(--text)] mb-3">{t("revenue_report")} · {t("by_method")}</h3>
          <div className="space-y-2">
            {rev.by_method.map(m => (
              <div key={m.method} className="flex justify-between border border-[var(--border-subtle)] rounded p-3">
                <span className="text-[var(--text)] capitalize">{t(m.method) || m.method}</span>
                <span className="gold-text font-medium num-display">{fmt(m.amount)}</span>
              </div>
            ))}
            {rev.by_method.length === 0 && <div className="text-sm text-[var(--text-muted)]">{t("no_data")}</div>}
          </div>
          <div className="gold-divider my-3"/>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--text-muted)]">{t("invoices")}: {fmtInt(rev.count)}</span>
            <span className="text-[var(--text-muted)]">{t("average_invoice")}: <span className="num-display">{fmt(rev.average)}</span></span>
          </div>
        </div>

        <div className="card-surface p-5">
          <h3 className="font-heading text-lg text-[var(--text)] mb-3">{t("expenses_report")} · {t("by_category")}</h3>
          <div className="space-y-2">
            {exp.by_category.map(c => (
              <div key={c.category} className="flex justify-between border border-[var(--border-subtle)] rounded p-3">
                <span className="text-[var(--text)]">{c.category}</span>
                <span className="rose-text font-medium num-display">{fmt(c.amount)}</span>
              </div>
            ))}
            {exp.by_category.length === 0 && <div className="text-sm text-[var(--text-muted)]">{t("no_data")}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== SALES REPORT ==============
const SalesReport = ({ range, branch, template }) => {
  const { t, lang } = useLanguage();
  const [s, setS] = useState(null);
  const [c, setC] = useState(null);
  // Category filter + item-kind filter — both apply to the same backend call.
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState("");
  const [kind, setKind] = useState(""); // "" | "service" | "product" | "package"

  // Load the category list once for the filter dropdown.
  useEffect(() => {
    api.get("/product-categories")
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const load = useCallback(async () => {
    const params = { start: range.start, end: range.end };
    if (branch) params.branch = branch;
    if (catId) params.category_id = catId;
    if (kind) params.kind = kind;
    const [a, b] = await Promise.all([
      api.get("/reports/sales-summary", { params }),
      api.get("/reports/top-customers", { params: { start: range.start, end: range.end, ...(branch ? { branch } : {}) } }),
    ]);
    setS(a.data); setC(b.data);
  }, [range.start, range.end, branch, catId, kind]);
  useEffect(() => { load(); }, [load]);

  // Print only the sales tables (top items + top customers) — never the
  // surrounding KPI chrome.
  const onPrint = () => {
    if (!s || !c) return;
    const isAr = lang === "ar";
    const filterMeta = [];
    if (catId) {
      const cat = categories.find((x) => x.category_id === catId);
      if (cat) filterMeta.push({ label: isAr ? "الفئة" : "Category", value: isAr ? cat.name_ar : cat.name_en });
    }
    if (kind) {
      const kindLbl = { service: isAr ? "خدمات" : "Services", product: isAr ? "منتجات" : "Products", package: isAr ? "باقات" : "Packages" }[kind];
      filterMeta.push({ label: isAr ? "النوع" : "Kind", value: kindLbl });
    }
    printReport({
      title: t("reports_sales"),
      subtitle: isAr ? "ملخص المبيعات وأفضل العملاء" : "Sales summary & top customers",
      lang,
      template,
      meta: [
        ...periodMeta(range, lang),
        ...filterMeta,
        { label: t("total"), value: fmt(s.total_sales) },
        { label: t("invoices"), value: fmtInt(s.invoice_count) },
        { label: t("average_invoice"), value: fmt(s.average_invoice) },
      ],
      columns: [t("item"), t("qty_sold"), t("amount")],
      rows: s.top_items.slice(0, 100).map(it => [
        it.name,
        { value: fmtInt(it.qty), className: "num" },
        { value: fmt(it.amount), className: "num pos" },
      ]),
      totals: [
        { label: t("total"), value: fmt(s.total_sales), className: "gold" },
        { label: t("invoices"), value: fmtInt(s.invoice_count) },
        { label: t("average_invoice"), value: fmt(s.average_invoice) },
      ],
      footerNote: isAr
        ? `أفضل عملاء: ${c.customers.slice(0, 5).map(cu => `${cu.customer_name} (${fmt(cu.total)})`).join(" · ")}`
        : `Top customers: ${c.customers.slice(0, 5).map(cu => `${cu.customer_name} (${fmt(cu.total)})`).join(" · ")}`,
    });
  };

  if (!s || !c) return <div className="text-[var(--text-muted)]">{t("loading")}</div>;

  return (
    <div className="space-y-5">
      {/* Sales-specific filters: category + item kind. Stacked above print button. */}
      <div className="card-surface p-4 flex flex-wrap items-end gap-3" data-testid="sales-filters">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-[var(--text-muted)] block mb-1">
            {lang === "ar" ? "الفئة" : "Category"}
          </label>
          <select className="input-dark" value={catId} onChange={(e) => setCatId(e.target.value)} data-testid="sales-category">
            <option value="">{lang === "ar" ? "كل الفئات" : "All categories"}</option>
            {categories.map((cat) => (
              <option key={cat.category_id} value={cat.category_id}>
                {lang === "ar" ? cat.name_ar : cat.name_en}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-[var(--text-muted)] block mb-1">
            {lang === "ar" ? "نوع البنود" : "Item kind"}
          </label>
          <select className="input-dark" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="sales-kind">
            <option value="">{lang === "ar" ? "الكل (خدمات + منتجات + باقات)" : "All (services + products + packages)"}</option>
            <option value="service">{lang === "ar" ? "خدمات فقط" : "Services only"}</option>
            <option value="product">{lang === "ar" ? "منتجات فقط" : "Products only"}</option>
            <option value="package">{lang === "ar" ? "باقات فقط" : "Packages only"}</option>
          </select>
        </div>
        {(catId || kind) && (
          <button onClick={() => { setCatId(""); setKind(""); }} data-testid="sales-clear-filters"
            className="px-3 py-2 rounded-md text-sm border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--rose)] hover:text-[var(--rose)]">
            {lang === "ar" ? "مسح الفلاتر" : "Clear filters"}
          </button>
        )}
        <div className="ms-auto">
          <PrintBtn onClick={onPrint} lang={lang}/>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Receipt} label={t("total")} value={fmt(s.total_sales)}/>
        <StatCard icon={Receipt} label={t("invoices")} value={fmtInt(s.invoice_count)} accent="rose"/>
        <StatCard icon={Receipt} label={t("average_invoice")} value={fmt(s.average_invoice)}/>
      </div>

      <div className="card-surface p-5">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3">{t("sales_summary")}</h3>
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr><th>{t("item")}</th><th>{t("qty_sold")}</th><th>{t("amount")}</th></tr></thead>
            <tbody>
              {s.top_items.slice(0, 30).map((it, i) => (
                <tr key={i}>
                  <td className="text-[var(--text)]">{it.name}</td>
                  <td className="text-[var(--text-muted)] num-display">{fmtInt(it.qty)}</td>
                  <td className="gold-text font-medium num-display">{fmt(it.amount)}</td>
                </tr>
              ))}
              {s.top_items.length === 0 && <tr><td colSpan={3} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface p-5">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3 flex items-center gap-2"><Users size={18}/>{t("top_customers")}</h3>
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr><th>{t("customer")}</th><th>{t("phone")}</th><th>{t("invoices")}</th><th>{t("total")}</th></tr></thead>
            <tbody>
              {c.customers.slice(0, 20).map((cu, i) => (
                <tr key={i}>
                  <td className="text-[var(--text)]">{cu.customer_name}</td>
                  <td className="text-[var(--text-muted)]" dir="ltr">{cu.customer_phone}</td>
                  <td className="text-[var(--text-muted)] num-display">{fmtInt(cu.invoice_count)}</td>
                  <td className="gold-text font-medium num-display">{fmt(cu.total)}</td>
                </tr>
              ))}
              {c.customers.length === 0 && <tr><td colSpan={4} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== INVENTORY REPORT ==============
const InventoryReport = ({ range, branch, template }) => {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState("");
  const [search, setSearch] = useState("");
  const [usePeriod, setUsePeriod] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (branch) params.branch = branch;
    if (catId) params.category_id = catId;
    if (search) params.search = search;
    if (usePeriod) {
      if (range.start) params.from_date = range.start;
      if (range.end) params.to_date = range.end;
    }
    const [r, c] = await Promise.all([
      api.get("/reports/inventory", { params }),
      api.get("/product-categories"),
    ]);
    setData(r.data); setCategories(c.data);
  }, [catId, search, usePeriod, range.start, range.end, branch]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[var(--text-muted)]">{t("loading")}</div>;
  const period = data.period;

  // Print only the inventory snapshot table (products, stock, cost, value).
  const onPrint = () => {
    const isAr = lang === "ar";
    const meta = [
      ...periodMeta(range, lang),
      { label: t("products_in_stock"), value: fmtInt(data.totals.products_count) },
      { label: t("stock"), value: fmtInt(data.totals.total_stock) },
      { label: t("stock_value_cost"), value: fmt(data.totals.value_at_cost) },
      { label: t("stock_value_retail"), value: fmt(data.totals.value_at_retail) },
    ];
    if (usePeriod) {
      meta.push({ label: isAr ? "مشتريات في الفترة" : "Purchased", value: fmtInt(data.totals.total_purchased_in_period || 0) });
      meta.push({ label: isAr ? "مبيعات في الفترة" : "Sold", value: fmtInt(data.totals.total_sold_in_period || 0) });
    }
    const cols = [t("name"), t("sku"), t("stock")];
    if (usePeriod) { cols.push(isAr ? "مشترى" : "Purchased"); cols.push(isAr ? "مباع" : "Sold"); }
    cols.push(t("cost"), t("price"), t("amount"));
    printReport({
      title: t("reports_inventory"),
      subtitle: isAr ? "لقطة جرد المخزون" : "Inventory snapshot",
      lang,
      template,
      meta,
      columns: cols,
      rows: data.products.map(p => {
        const row = [
          isAr ? p.name_ar : p.name_en,
          p.sku || "—",
          { value: fmtInt(p.stock), className: p.stock <= p.low_stock_threshold ? "num neg" : "num" },
        ];
        if (usePeriod) {
          row.push({ value: fmtInt(p.purchased_in_period || 0), className: "num" });
          row.push({ value: fmtInt(p.sold_in_period || 0), className: "num" });
        }
        row.push({ value: fmt(p.cost), className: "num" });
        row.push({ value: fmt(p.price), className: "num" });
        row.push({ value: fmt((p.stock || 0) * (p.cost || 0)), className: "num pos" });
        return row;
      }),
      totals: [
        { label: t("stock_value_cost"), value: fmt(data.totals.value_at_cost), className: "gold" },
        { label: t("stock_value_retail"), value: fmt(data.totals.value_at_retail) },
        { label: t("products_in_stock"), value: fmtInt(data.totals.products_count) },
      ],
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end no-print"><PrintBtn onClick={onPrint} lang={lang}/></div>
      <div className="card-surface p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-[var(--text-muted)] block mb-1">{t("category")}</label>
          <select className="input-dark" value={catId} onChange={e => setCatId(e.target.value)} data-testid="inv-category">
            <option value="">{t("all_categories")}</option>
            {categories.map(c => <option key={c.category_id} value={c.category_id}>{lang === "ar" ? c.name_ar : c.name_en}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-[var(--text-muted)] block mb-1">{t("search_product")}</label>
          <div className="relative">
            <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)]"/>
            <input className="input-dark ps-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="inv-search"/>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--text)] pb-1 cursor-pointer">
          <input type="checkbox" checked={usePeriod} onChange={e => setUsePeriod(e.target.checked)} data-testid="inv-period-toggle"/>
          {t("period_filter") || (lang === "ar" ? "فلتر الفترة (مشتريات/مبيعات)" : "Period filter (purchases/sales)")}
        </label>
      </div>

      {usePeriod && period && (
        <div className="text-xs text-[var(--text-muted)] num-display">{lang === "ar" ? "الفترة:" : "Period:"} {period.from_date || "—"} → {period.to_date || "—"}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={PackageIcon} label={t("products_in_stock")} value={fmtInt(data.totals.products_count)}/>
        <StatCard icon={PackageIcon} label={t("stock")} value={fmtInt(data.totals.total_stock)} accent="rose"/>
        <StatCard icon={Wallet} label={t("stock_value_cost")} value={fmt(data.totals.value_at_cost)}/>
        <StatCard icon={Wallet} label={t("stock_value_retail")} value={fmt(data.totals.value_at_retail)} accent="rose"/>
      </div>

      {usePeriod && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={TrendingUp} label={lang === "ar" ? "مشتريات في الفترة" : "Purchased in period"} value={fmtInt(data.totals.total_purchased_in_period || 0)} testId="inv-period-purchased"/>
          <StatCard icon={TrendingDown} label={lang === "ar" ? "مبيعات في الفترة" : "Sold in period"} value={fmtInt(data.totals.total_sold_in_period || 0)} accent="rose" testId="inv-period-sold"/>
        </div>
      )}

      <div className="card-surface p-5">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3">{t("by_category")}</h3>
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr><th>{t("category")}</th><th>{t("products_count")}</th><th>{t("stock")}</th><th>{t("stock_value_cost")}</th></tr></thead>
            <tbody>
              {data.by_category.map(c => (
                <tr key={c.category_id}>
                  <td className="text-[var(--text)]">{lang === "ar" ? c.name_ar : c.name_en}</td>
                  <td className="text-[var(--text-muted)] num-display">{fmtInt(c.products)}</td>
                  <td className="text-[var(--text-muted)] num-display">{fmtInt(c.stock)}</td>
                  <td className="gold-text font-medium num-display">{fmt(c.value_cost)}</td>
                </tr>
              ))}
              {data.by_category.length === 0 && <tr><td colSpan={4} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface p-5">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3">{t("inventory_snapshot")}</h3>
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr>
              <th>{t("name")}</th><th>{t("sku")}</th><th>{t("stock")}</th>
              {usePeriod && <th>{lang === "ar" ? "مشترى" : "Purchased"}</th>}
              {usePeriod && <th>{lang === "ar" ? "مباع" : "Sold"}</th>}
              <th>{t("cost")}</th><th>{t("price")}</th><th>{t("amount")}</th>
            </tr></thead>
            <tbody>
              {data.products.map(p => (
                <tr key={p.product_id}>
                  <td className="text-[var(--text)]">{lang === "ar" ? p.name_ar : p.name_en}</td>
                  <td className="text-[var(--text-muted)]">{p.sku}</td>
                  <td><span className={`badge ${p.stock <= p.low_stock_threshold ? "badge-red" : "badge-green"}`}>{fmtInt(p.stock)}</span></td>
                  {usePeriod && <td className="num-display text-[var(--text-muted)]">{fmtInt(p.purchased_in_period || 0)}</td>}
                  {usePeriod && <td className="num-display text-[var(--text-muted)]">{fmtInt(p.sold_in_period || 0)}</td>}
                  <td className="text-[var(--text-muted)] num-display">{fmt(p.cost)}</td>
                  <td className="text-[var(--text)] num-display">{fmt(p.price)}</td>
                  <td className="gold-text font-medium num-display">{fmt(p.stock * p.cost)}</td>
                </tr>
              ))}
              {data.products.length === 0 && <tr><td colSpan={usePeriod ? 8 : 6} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN ==============
export default function Reports() {
  const { t, lang } = useLanguage();
  const { branches, activeBranchId } = useBranch();
  const template = usePrintTemplate();
  const [tab, setTab] = useState("accounts");
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(dateRangePresets("month"));
  // Branch scope for reports — default to current active branch. "all" combines
  // every branch and is ONLY used when the operator explicitly selects it.
  const [scope, setScope] = useState(activeBranchId || "");
  useEffect(() => {
    // Track changes to the global active branch (e.g. user switches branch in topbar).
    if (activeBranchId && !scope) setScope(activeBranchId);
  }, [activeBranchId, scope]);
  const branchLabel = (b) => (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar)) || b.branch_id;
  // Build the `branch` query param: "all" -> "all", any branch -> its id, "" -> omit (defer to header).
  const branchParam = scope || activeBranchId || "";

  const tabs = [
    { id: "accounts", label: t("reports_accounts") },
    { id: "sales", label: t("reports_sales") },
    { id: "inventory", label: t("reports_inventory") },
    { id: "purchases", label: t("purchases_report") },
    { id: "discounts", label: lang === "ar" ? "الخصومات" : "Discounts" },
    { id: "visit-duration", label: t("visit_duration") },
    { id: "employee-performance", label: t("employee_performance") },
    { id: "active-service-hours", label: t("active_service_hours") },
  ];

  const tabExports = {
    accounts: { excel: "profit-loss", pdf: "profit-loss" },
    sales: { excel: "sales-summary" },
    inventory: { excel: "inventory", pdf: "inventory" },
    purchases: { excel: "purchases", pdf: "purchases" },
  };
  const activeExport = tabExports[tab];
  const exportParams = { start: range.start, end: range.end, ...(branchParam ? { branch: branchParam } : {}) };

  const downloadExcel = async (reportType) => {
    try {
      const search = new URLSearchParams(exportParams).toString();
      const url = `${API_BASE}/reports/export/${reportType}${search ? `?${search}` : ""}`;
      await downloadFile(url, `${reportType}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(t("excel_exported"));
    } catch {
      toast.error(lang === "ar" ? "فشل التصدير" : "Export failed");
    }
  };

  const downloadPDF = async (reportType) => {
    try {
      const search = new URLSearchParams(exportParams).toString();
      const url = `${API_BASE}/reports/pdf/${reportType}${search ? `?${search}` : ""}`;
      await downloadFile(url, `${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(lang === "ar" ? "تم تصدير PDF" : "PDF exported");
    } catch {
      toast.error(lang === "ar" ? "فشل تصدير PDF" : "PDF export failed");
    }
  };

  // NOTE: Each sub-report owns its own "Print Report" button (above its
  // KPIs) that calls `printReport` from `lib/printReport.js` with structured
  // data — no more screen-capture printing. The global print button was
  // removed because it produced a verbose "screenshot of the dashboard"
  // output the operator rejected.

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <h1 className="font-heading text-4xl gold-text">{t("nav_reports")}</h1>
        <div className="flex flex-wrap gap-2">
          {activeExport?.excel && (
            <button onClick={() => downloadExcel(activeExport.excel)}
              data-testid={`export-${activeExport.excel}`}
              className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
              <Download size={14}/>Excel
            </button>
          )}
          {activeExport?.pdf && (
            <button onClick={() => downloadPDF(activeExport.pdf)}
              data-testid={`pdf-${activeExport.pdf}`}
              className="btn-gold px-3 py-2 rounded-md text-sm flex items-center gap-2">
              <Download size={14}/>{t("export_pdf")}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(x => (
          <button key={x.id} onClick={() => setTab(x.id)} data-testid={`tab-${x.id}`}
            className={`px-5 py-2.5 rounded-md text-sm border ${tab === x.id ? "btn-gold" : "border-[var(--border)] text-[var(--text)] bg-[var(--surface)]"}`}>
            {x.label}
          </button>
        ))}
      </div>

      {<RangePicker range={range} setRange={setRange} preset={preset} setPreset={setPreset}/>}

      {/* Branch scope selector — reports default to the currently active branch.
          Combined view across all branches requires explicitly choosing "All branches". */}
      <div className="card-surface p-4 flex flex-wrap items-center gap-3" data-testid="reports-branch-picker">
        <Building2 size={16} className="text-[var(--gold)]"/>
        <span className="text-sm text-[var(--text-muted)]">
          {lang === "ar" ? "نطاق التقرير:" : "Report scope:"}
        </span>
        <div className="flex flex-wrap gap-2">
          {branches.map((b) => (
            <button key={b.branch_id} onClick={() => setScope(b.branch_id)} data-testid={`scope-branch-${b.branch_id}`}
              className={`px-3 py-2 rounded-md text-sm border ${scope === b.branch_id ? "btn-gold" : "border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:border-[var(--gold)]"}`}>
              {branchLabel(b)}
            </button>
          ))}
          <button onClick={() => setScope("all")} data-testid="scope-branch-all"
            className={`px-3 py-2 rounded-md text-sm border ${scope === "all" ? "btn-gold" : "border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:border-[var(--gold)]"}`}>
            {lang === "ar" ? "جميع الفروع" : "All Branches"}
          </button>
        </div>
        {scope === "all" && (
          <span className="text-xs text-[var(--gold)] ms-auto">
            {lang === "ar" ? "عرض مجمع لجميع الفروع" : "Combined view — all branches"}
          </span>
        )}
      </div>

      {tab === "accounts" && <AccountsReport range={range} branch={branchParam} template={template}/>}
      {tab === "sales" && <SalesReport range={range} branch={branchParam} template={template}/>}
      {tab === "inventory" && <InventoryReport range={range} branch={branchParam} template={template}/>}
      {tab === "purchases" && <PurchasesReport range={range} branch={branchParam} template={template}/>}
      {tab === "discounts" && <DiscountReport range={range} template={template}/>}
      {tab === "visit-duration" && <VisitDurationReport range={range} branch={branchParam}/>}
      {tab === "employee-performance" && <EmployeePerformanceReport range={range} branch={branchParam}/>}
      {tab === "active-service-hours" && <ActiveServiceHoursReport range={range} branch={branchParam}/>}
    </div>
  );
}

// ============== PURCHASES REPORT ==============
const PurchasesReport = ({ range, branch, template }) => {
  const { t, lang } = useLanguage();
  const [data, setData] = useState([]);
  useEffect(() => { (async () => {
    // The /purchases endpoint already honours X-Branch-Id for the active branch
    // and accepts a `branch` query param ("all" or specific id) for explicit scope.
    const params = {};
    if (branch) params.branch = branch;
    const r = await api.get("/purchases", { params });
    const filtered = r.data.filter(p => (!range.start || p.date >= range.start) && (!range.end || p.date <= range.end));
    setData(filtered);
  })(); }, [range.start, range.end, branch]);

  const total = data.reduce((s, p) => s + (p.total || 0), 0);
  const totalDisc = data.reduce((s, p) => s + (p.overall_discount || 0), 0);

  const onPrint = () => {
    const isAr = lang === "ar";
    printReport({
      title: t("purchases_report") || (isAr ? "تقرير المشتريات" : "Purchases Report"),
      subtitle: isAr ? "قائمة فواتير الشراء خلال الفترة" : "Purchase invoices within the period",
      lang,
      template,
      meta: [
        ...periodMeta(range, lang),
        { label: t("invoices"), value: fmtInt(data.length) },
        { label: t("total"), value: fmt(total) },
        { label: t("discount"), value: fmt(totalDisc) },
      ],
      columns: [t("purchase_number"), t("date"), t("supplier"), t("items_count"), t("subtotal"), t("discount"), t("grand_total")],
      rows: data.map(p => [
        p.purchase_number,
        p.date,
        p.supplier || "—",
        { value: fmtInt(p.items?.length || 0), className: "num" },
        { value: fmt(p.subtotal || p.total), className: "num" },
        { value: fmt(p.overall_discount || 0), className: "num neg" },
        { value: fmt(p.total), className: "num pos" },
      ]),
      totals: [
        { label: t("total"), value: fmt(total), className: "gold" },
        { label: t("discount"), value: fmt(totalDisc), className: "rose" },
        { label: t("invoices"), value: fmtInt(data.length) },
      ],
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end no-print"><PrintBtn onClick={onPrint} lang={lang}/></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Receipt} label={t("purchase_report")} value={fmtInt(data.length)}/>
        <StatCard icon={Wallet} label={t("total")} value={fmt(total)}/>
        <StatCard icon={TrendingDown} label={t("discount")} value={fmt(totalDisc)} accent="rose"/>
      </div>
      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead><tr>
            <th>{t("purchase_number")}</th><th>{t("date")}</th><th>{t("supplier")}</th><th>{t("items_count")}</th><th>{t("subtotal")}</th><th>{t("discount")}</th><th>{t("grand_total")}</th>
          </tr></thead>
          <tbody>
            {data.map(p => (
              <tr key={p.purchase_id}>
                <td className="text-[var(--text)]">{p.purchase_number}</td>
                <td className="text-[var(--text-muted)]">{p.date}</td>
                <td className="text-[var(--text-muted)]">{p.supplier || "-"}</td>
                <td className="text-[var(--text-muted)] num-display">{p.items?.length || 0}</td>
                <td className="text-[var(--text)] num-display">{fmt(p.subtotal || p.total)}</td>
                <td className="rose-text num-display">{fmt(p.overall_discount || 0)}</td>
                <td className="gold-text font-medium num-display">{fmt(p.total)}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={7} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
